import React from "react";
import { Box } from "@mui/material";
import Webcam from "react-webcam";
import { isMobile } from "react-device-detect";
import * as tf from "@tensorflow/tfjs";
import { calculateSharpness, cropAndNormalize } from "../../utils/image-util";
import { useOpenCv } from "opencv-react";

const ocrVideoConstraints = {
  width: { ideal: 1920 },
  height: { ideal: isMobile ? 1440 : 1080 },
  aspectRatio: { ideal: isMobile ? 1.333333333 : 1.777777778 },
  facingMode: { ideal: isMobile ? "environment" : "user" },
};

export default function CardDetectionCamera(props) {
  const [mediaStream, setMediaStream] = React.useState(false);
  const { cv } = useOpenCv();
  const cameraRef = React.useRef(null);
  const handleUserMediaError = () => {
    props.handleClose();
    props.handleAlert("Không kết nối được đến thiết bị chụp ảnh");
  };

  const handleGetUserMedia = () => {
    setMediaStream(true);
    const model = props.cardDetection;
    detectCard(model);
  };

  const detectCard = async (model) => {
    if (
      cameraRef.current !== null &&
      cameraRef.current !== undefined &&
      cameraRef.current.video !== null &&
      cameraRef.current.video !== undefined
    ) {
      // Video width and height
      const width = cameraRef.current.video.videoWidth;
      const height = cameraRef.current.video.videoHeight;

      // The width and height of the crop region of the image.
      // Note that this region is 5% bigger than the box displayed on the screen
      // to allow some margin of error.
      const cropWidth = (width * (isMobile ? 95 : 75)) / 100;
      const cropHeight = cropWidth / 1.586;

      // Get the Webcam's canvas element
      const webcamCanvas = cameraRef.current.getCanvas({
        width: width,
        height: height,
      });

      // Save the current video frame to a temporary canvas
      let canvas = document.createElement("canvas");
      canvas.width = webcamCanvas.width;
      canvas.height = webcamCanvas.height;

      let ctx = canvas.getContext("2d");
      ctx.drawImage(webcamCanvas, 0, 0);

      // Processing the current frame
      let isDetected = false;

      tf.engine().startScope();

      let [modelWidth, modelHeight] = model.inputs[0].shape.slice(1, 3);
      const input = tf.tidy(() => {
        return tf.image
          .resizeBilinear(tf.browser.fromPixels(canvas), [
            modelWidth,
            modelHeight,
          ])
          .div(255.0)
          .expandDims(0);
      });

      await model.executeAsync(input).then((res) => {
        const [boxes, scores] = res.slice(0, 2);

        const scores_data = scores.dataSync();
        const boxes_data = boxes.dataSync();

        if (scores_data[0] > 0.8) {
          let [x1, y1, x2, y2] = boxes_data.slice(0, 4);
          x1 *= width;
          x2 *= width;
          y1 *= height;
          y2 *= height;

          if (
            x1 >= (width - cropWidth) / 2 &&
            y1 >= (height - cropHeight) / 2 &&
            x2 <= (width + cropWidth) / 2 &&
            y2 <= (height + cropHeight) / 2
          ) {
            // Crop the image (using the model box, not the on-screen box)
            // and normalize the result so that the longer edge is 800
            const normCanvas = cropAndNormalize(canvas, 800, x1, y1, x2, y2);

            const sharpness = calculateSharpness(
              cv,
              normCanvas
                .getContext("2d")
                .getImageData(0, 0, normCanvas.width, normCanvas.height)
            );
            if (sharpness >= 135) {
              isDetected = true;
            }
          }
        }

        tf.dispose(res);
      });

      if (!isDetected) {
        requestAnimationFrame(() => detectCard(model));
      } else {
        props.handleClose();
        props.onTakingImage(
          canvas.toDataURL("image/jpeg", 0.95),
          width,
          height,
          cropWidth,
          cropHeight
        );
      }

      tf.engine().endScope();
    }
  };

  let mask;
  if (mediaStream) {
    mask = (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderColor: "#7FFF00",
          borderStyle: "solid",
          width: isMobile ? "90%" : "70%", // The box size is actually 5% smaller than crop size to allow some margin of error.
          height: "0",
          paddingBottom: isMobile ? "56.747%" : "44.136%",
          boxShadow: "0 0 0 600px rgba(0, 0, 0, 0.6)",
        }}
      />
    );
  } else {
    mask = (
      <Box
        sx={{
          width: "100%",
          paddingBottom: isMobile ? "133.333333333%" : "56.25%",
          height: 0,
          backgroundColor: "black",
        }}
      />
    );
  }

  return (
    <>
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <Webcam
          videoConstraints={ocrVideoConstraints}
          screenshotFormat="image/jpeg"
          ref={cameraRef}
          onUserMediaError={handleUserMediaError}
          style={
            mediaStream
              ? {
                  width: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                  backgroundColor: "black",
                }
              : {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "1%",
                }
          }
          onPlay={handleGetUserMedia}
        />
        {mask}
      </Box>
    </>
  );
}
